'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { Award01IconSolid, Rocket01IconSolid } from '@/components/ui/icon/glyphs';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/ui/utils';
import type { LifecycleModalPayload, LifecycleVariant } from '@/stores/market-lifecycle-modal';
export interface LifecycleModalViewProps {
  payload: LifecycleModalPayload;
  /** Dismiss the modal before the selected CTA continues. */
  onClose: () => void;
}
type Tone = 'positive' | 'negative' | 'brand';
type PrimaryCta = 'trade' | 'claim' | 'browse';
interface VariantPresentation {
  tone: Tone;
  heroIcon: IconSvgElement;
  /** Show the winning-outcome label chip (resolution variants only). */
  showWinnerChip: boolean;
  primary: PrimaryCta;
  /** Show the secondary "Claim backer rewards" CTA. */
  showBackerCta: boolean;
}
const TONE_CLASSES: Record<Tone, {
  glow: string;
  iconWrap: string;
  icon: string;
}> = {
  positive: {
    glow: 'lifecycle-glow-positive',
    iconWrap: 'bg-feedback-success-surface',
    icon: 'text-feedback-success-content'
  },
  negative: {
    glow: 'lifecycle-glow-negative',
    iconWrap: 'bg-feedback-error-surface',
    icon: 'text-feedback-error-content'
  },
  brand: {
    glow: 'lifecycle-glow-brand',
    iconWrap: 'bg-background-brand',
    icon: 'text-foreground-brand'
  }
};
function toneForVariant(variant: LifecycleVariant): Tone {
  switch (variant) {
    case 'resolution-won-nonbacker':
    case 'resolution-won-backer':
      return 'positive';
    case 'resolution-lost-nonbacker':
    case 'resolution-lost-backer':
      return 'negative';
    default:
      return 'brand';
  }
}
function presentationFor(variant: LifecycleVariant, isBacker: boolean): VariantPresentation {
  const tone = toneForVariant(variant);
  switch (variant) {
    case 'graduation-generic':
    case 'graduation-backer':
    case 'graduation-creator':
      return {
        tone,
        heroIcon: Rocket01IconSolid,
        showWinnerChip: false,
        primary: 'trade',
        showBackerCta: false
      };
    case 'resolution-won-nonbacker':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'claim',
        showBackerCta: false
      };
    case 'resolution-won-backer':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'claim',
        showBackerCta: true
      };
    case 'resolution-lost-nonbacker':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: false
      };
    case 'resolution-lost-backer':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: true
      };
    case 'resolution-not-holder':
      // A pure backer with no outcome holdings still gets the backer claim path.
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: isBacker
      };
  }
}

/**
 * Presentational renderer for the lifecycle-modal variants. Branches on
 * `payload.variant` to pick a tone (corner glow), hero icon, copy, and the
 * primary / secondary CTAs. No animation is added here, so reduced-motion is
 * satisfied by construction (the only motion is SilkSheet's own overlay
 * transition, which already honors `prefers-reduced-motion`).
 *
 * NOTE (spec 112, FR-018): the hero is currently an icon-based placeholder.
 * When the final Figma art lands, swap the hero for the winning-outcome image
 * — render it via `OptimizedImage`/`OutcomeIndicator` so the NSFW overlay is
 * threaded (the `winningOutcome*` payload fields are carried for that purpose).
 */
export function LifecycleModalView(t0) {
  const $ = _c(112);
  const {
    payload,
    onClose
  } = t0;
  const t = useTranslations("market.lifecycle_modal");
  let t1;
  if ($[0] !== payload.isBacker || $[1] !== payload.variant) {
    t1 = presentationFor(payload.variant, payload.isBacker);
    $[0] = payload.isBacker;
    $[1] = payload.variant;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const p = t1;
  const tone = TONE_CLASSES[p.tone];
  const label = payload.winningOutcomeLabel ?? "";
  const t2 = payload.winnerOutcomeIndex ?? 0;
  let t3;
  if ($[3] !== label || $[4] !== t2) {
    t3 = {
      title: label,
      index: t2
    };
    $[3] = label;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== payload.marketPubkey) {
    t4 = {
      marketPubkey: payload.marketPubkey
    };
    $[6] = payload.marketPubkey;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const winnerColor = useOutcomeColor(t3, t4);
  let t5;
  bb0: {
    switch (payload.variant) {
      case "graduation-generic":
        {
          let t6;
          if ($[8] !== t) {
            t6 = t("graduationGeneric.headline");
            $[8] = t;
            $[9] = t6;
          } else {
            t6 = $[9];
          }
          let t7;
          if ($[10] !== t) {
            t7 = t("graduationGeneric.body");
            $[10] = t;
            $[11] = t7;
          } else {
            t7 = $[11];
          }
          let t8;
          if ($[12] !== t6 || $[13] !== t7) {
            t8 = {
              headline: t6,
              body: t7
            };
            $[12] = t6;
            $[13] = t7;
            $[14] = t8;
          } else {
            t8 = $[14];
          }
          t5 = t8;
          break bb0;
        }
      case "graduation-creator":
        {
          let t6;
          if ($[15] !== t) {
            t6 = t("graduationCreator.headline");
            $[15] = t;
            $[16] = t6;
          } else {
            t6 = $[16];
          }
          let t7;
          if ($[17] !== t) {
            t7 = t("graduationCreator.body");
            $[17] = t;
            $[18] = t7;
          } else {
            t7 = $[18];
          }
          let t8;
          if ($[19] !== t6 || $[20] !== t7) {
            t8 = {
              headline: t6,
              body: t7
            };
            $[19] = t6;
            $[20] = t7;
            $[21] = t8;
          } else {
            t8 = $[21];
          }
          t5 = t8;
          break bb0;
        }
      case "graduation-backer":
        {
          let t6;
          if ($[22] !== t) {
            t6 = t("graduationBacker.headline");
            $[22] = t;
            $[23] = t6;
          } else {
            t6 = $[23];
          }
          let t7;
          if ($[24] !== t) {
            t7 = t("graduationBacker.body");
            $[24] = t;
            $[25] = t7;
          } else {
            t7 = $[25];
          }
          let t8;
          if ($[26] !== t6 || $[27] !== t7) {
            t8 = {
              headline: t6,
              body: t7
            };
            $[26] = t6;
            $[27] = t7;
            $[28] = t8;
          } else {
            t8 = $[28];
          }
          t5 = t8;
          break bb0;
        }
      case "resolution-won-nonbacker":
        {
          let t6;
          if ($[29] !== t) {
            t6 = t("wonNonbacker.headline");
            $[29] = t;
            $[30] = t6;
          } else {
            t6 = $[30];
          }
          let t7;
          if ($[31] !== label || $[32] !== t) {
            t7 = t("wonNonbacker.body", {
              label
            });
            $[31] = label;
            $[32] = t;
            $[33] = t7;
          } else {
            t7 = $[33];
          }
          let t8;
          if ($[34] !== t6 || $[35] !== t7) {
            t8 = {
              headline: t6,
              body: t7
            };
            $[34] = t6;
            $[35] = t7;
            $[36] = t8;
          } else {
            t8 = $[36];
          }
          t5 = t8;
          break bb0;
        }
      case "resolution-won-backer":
        {
          let t6;
          if ($[37] !== t) {
            t6 = t("wonBacker.headline");
            $[37] = t;
            $[38] = t6;
          } else {
            t6 = $[38];
          }
          let t7;
          if ($[39] !== label || $[40] !== t) {
            t7 = t("wonBacker.body", {
              label
            });
            $[39] = label;
            $[40] = t;
            $[41] = t7;
          } else {
            t7 = $[41];
          }
          let t8;
          if ($[42] !== t6 || $[43] !== t7) {
            t8 = {
              headline: t6,
              body: t7
            };
            $[42] = t6;
            $[43] = t7;
            $[44] = t8;
          } else {
            t8 = $[44];
          }
          t5 = t8;
          break bb0;
        }
      case "resolution-lost-nonbacker":
        {
          let t6;
          if ($[45] !== t) {
            t6 = t("lostNonbacker.headline");
            $[45] = t;
            $[46] = t6;
          } else {
            t6 = $[46];
          }
          let t7;
          if ($[47] !== label || $[48] !== t) {
            t7 = t("lostNonbacker.body", {
              label
            });
            $[47] = label;
            $[48] = t;
            $[49] = t7;
          } else {
            t7 = $[49];
          }
          let t8;
          if ($[50] !== t6 || $[51] !== t7) {
            t8 = {
              headline: t6,
              body: t7
            };
            $[50] = t6;
            $[51] = t7;
            $[52] = t8;
          } else {
            t8 = $[52];
          }
          t5 = t8;
          break bb0;
        }
      case "resolution-lost-backer":
        {
          let t6;
          if ($[53] !== t) {
            t6 = t("lostBacker.headline");
            $[53] = t;
            $[54] = t6;
          } else {
            t6 = $[54];
          }
          let t7;
          if ($[55] !== label || $[56] !== t) {
            t7 = t("lostBacker.body", {
              label
            });
            $[55] = label;
            $[56] = t;
            $[57] = t7;
          } else {
            t7 = $[57];
          }
          let t8;
          if ($[58] !== t6 || $[59] !== t7) {
            t8 = {
              headline: t6,
              body: t7
            };
            $[58] = t6;
            $[59] = t7;
            $[60] = t8;
          } else {
            t8 = $[60];
          }
          t5 = t8;
          break bb0;
        }
      case "resolution-not-holder":
        {
          let t6;
          if ($[61] !== t) {
            t6 = t("notHolder.headline");
            $[61] = t;
            $[62] = t6;
          } else {
            t6 = $[62];
          }
          let t7;
          if ($[63] !== label || $[64] !== t) {
            t7 = t("notHolder.body", {
              label
            });
            $[63] = label;
            $[64] = t;
            $[65] = t7;
          } else {
            t7 = $[65];
          }
          let t8;
          if ($[66] !== t6 || $[67] !== t7) {
            t8 = {
              headline: t6,
              body: t7
            };
            $[66] = t6;
            $[67] = t7;
            $[68] = t8;
          } else {
            t8 = $[68];
          }
          t5 = t8;
          break bb0;
        }
    }
    t5 = undefined;
  }
  const copy = t5;
  const claim = payload.claim;
  const claimClaimed = claim?.claimed === true;
  const claimReady = claim?.finalized === true && claim?.claimable === true && !claimClaimed;
  let t6;
  if ($[69] !== claimClaimed || $[70] !== claimReady || $[71] !== t) {
    t6 = claimClaimed ? t("cta.claimed") : claimReady ? t("cta.claim") : t("cta.claimPending");
    $[69] = claimClaimed;
    $[70] = claimReady;
    $[71] = t;
    $[72] = t6;
  } else {
    t6 = $[72];
  }
  const claimLabel = t6;
  let t7;
  if ($[73] !== tone.iconWrap) {
    t7 = cn("flex size-16 items-center justify-center rounded-full", tone.iconWrap);
    $[73] = tone.iconWrap;
    $[74] = t7;
  } else {
    t7 = $[74];
  }
  const t8 = p.heroIcon;
  let t9;
  if ($[75] !== tone.icon) {
    t9 = cn("size-8", tone.icon);
    $[75] = tone.icon;
    $[76] = t9;
  } else {
    t9 = $[76];
  }
  let t10;
  if ($[77] !== p.heroIcon || $[78] !== t9) {
    t10 = <Icon icon={t8} className={t9} aria-hidden="true" />;
    $[77] = p.heroIcon;
    $[78] = t9;
    $[79] = t10;
  } else {
    t10 = $[79];
  }
  let t11;
  if ($[80] !== t10 || $[81] !== t7) {
    t11 = <div className={t7}>{t10}</div>;
    $[80] = t10;
    $[81] = t7;
    $[82] = t11;
  } else {
    t11 = $[82];
  }
  let t12;
  if ($[83] !== copy.headline) {
    t12 = <h2 className="text-heading font-bold text-foreground-primary">{copy.headline}</h2>;
    $[83] = copy.headline;
    $[84] = t12;
  } else {
    t12 = $[84];
  }
  let t13;
  if ($[85] !== copy.body) {
    t13 = <p className="text-body text-foreground-secondary">{copy.body}</p>;
    $[85] = copy.body;
    $[86] = t13;
  } else {
    t13 = $[86];
  }
  let t14;
  if ($[87] !== t12 || $[88] !== t13) {
    t14 = <div className="flex flex-col items-center gap-2">{t12}{t13}</div>;
    $[87] = t12;
    $[88] = t13;
    $[89] = t14;
  } else {
    t14 = $[89];
  }
  let t15;
  if ($[90] !== p.showWinnerChip || $[91] !== payload.winningOutcomeLabel || $[92] !== winnerColor) {
    t15 = p.showWinnerChip && payload.winningOutcomeLabel !== null ? <span className={cn("inline-flex w-fit items-center gap-1.5 rounded-pill px-2.5 py-1", winnerColor.outline)}><Icon icon={Award01IconSolid} className={cn("size-3.5", winnerColor.text)} aria-hidden="true" /><span className={cn("text-caption font-medium", winnerColor.text)}>{payload.winningOutcomeLabel}</span></span> : null;
    $[90] = p.showWinnerChip;
    $[91] = payload.winningOutcomeLabel;
    $[92] = winnerColor;
    $[93] = t15;
  } else {
    t15 = $[93];
  }
  let t16;
  if ($[94] !== claimLabel || $[95] !== claimReady || $[96] !== onClose || $[97] !== p.primary || $[98] !== t) {
    t16 = p.primary === "browse" ? <AnimatedButton asChild={true} variant="primary" size="lg" className="w-full"><Link href="/markets" onClick={onClose}>{t("cta.browse")}</Link></AnimatedButton> : p.primary === "trade" ? <AnimatedButton variant="primary" size="lg" className="w-full" onClick={onClose}>{t("cta.trade")}</AnimatedButton> : <AnimatedButton variant="primary" size="lg" className="w-full" onClick={onClose} disabled={!claimReady}>{claimLabel}</AnimatedButton>;
    $[94] = claimLabel;
    $[95] = claimReady;
    $[96] = onClose;
    $[97] = p.primary;
    $[98] = t;
    $[99] = t16;
  } else {
    t16 = $[99];
  }
  let t17;
  if ($[100] !== onClose || $[101] !== p.showBackerCta || $[102] !== t) {
    t17 = p.showBackerCta ? <AnimatedButton variant="secondary" size="lg" className="w-full" onClick={onClose}>{t("cta.claimBackerRewards")}</AnimatedButton> : null;
    $[100] = onClose;
    $[101] = p.showBackerCta;
    $[102] = t;
    $[103] = t17;
  } else {
    t17 = $[103];
  }
  let t18;
  if ($[104] !== t16 || $[105] !== t17) {
    t18 = <div className="flex w-full flex-col gap-2 pt-2">{t16}{t17}</div>;
    $[104] = t16;
    $[105] = t17;
    $[106] = t18;
  } else {
    t18 = $[106];
  }
  let t19;
  if ($[107] !== t11 || $[108] !== t14 || $[109] !== t15 || $[110] !== t18) {
    t19 = <div className="relative flex flex-col items-center gap-4 text-center">{t11}{t14}{t15}{t18}</div>;
    $[107] = t11;
    $[108] = t14;
    $[109] = t15;
    $[110] = t18;
    $[111] = t19;
  } else {
    t19 = $[111];
  }
  return t19;
}